import React, { ReactNode } from "react";
import styles from "./AdminTable.module.scss";
import Table, {
  BodyColumn,
  HeaderColumn,
  SortingState,
} from "@src/components/table/Table";
import { Pagination } from "antd";
import Spinner from "@src/components/spinner/Spinner";

type AdminTableProps<T> = {
  header: HeaderColumn[];
  body: BodyColumn<T>[];
  data: T[];
  keyExtractor?: (item: T) => string;
  loading?: boolean;
  currentPage?: number;
  perPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  noItemsText?: string;
  color?: "grey" | "green";
  sorting?: SortingState | undefined;
  onSortingChange?: (next: SortingState | undefined) => void;
};

/**
 * AdminTable - An enhanced table component for admin pages
 *
 * This component wraps the base Table component and adds:
 * - Loading state
 * - Pagination
 * - Consistent styling
 */
function AdminTable<T>({
  header,
  body,
  data,
  keyExtractor,
  loading = false,
  currentPage,
  perPage,
  totalItems,
  onPageChange,
  noItemsText = "No items found.",
  color = "green",
  sorting,
  onSortingChange,
}: AdminTableProps<T>) {
  const showPagination =
    currentPage !== undefined &&
    perPage !== undefined &&
    totalItems !== undefined &&
    onPageChange !== undefined;

  return (
    <div className={styles.tableContainer}>
      {loading ? (
        <div className={styles.loadingContainer}>
          <Spinner />
        </div>
      ) : (
        <>
          <Table
            header={header}
            body={body}
            data={data}
            keyExtractor={keyExtractor}
            color={color}
            noItemsText={noItemsText}
            smallFont={true}
            rowClass="adminTableRow"
            sorting={sorting}
            onSortingChange={onSortingChange}
          />

          {showPagination && data.length > 0 && (
            <div className={styles.paginationWrapper}>
              <div className={styles.paginationContainer}>
                <Pagination
                  current={currentPage}
                  pageSize={perPage}
                  total={totalItems}
                  onChange={onPageChange}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  showTotal={(total, range) =>
                    `Showing ${range[0]}-${range[1]} of ${total}`
                  }
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminTable;
